import React from "react"
import { useNavigate, useLocation } from "react-router-dom"
import companylogo from "../../asset/companylogo.png"
import { Users, Building, Package, ShoppingCart } from "lucide-react"
import LogoutButton from "../components/button/LogoutButton"
import { container } from "../../infrastructure/container"
import { ApiService } from "../../infrastructure/services/api"
import { Logger } from "../../infrastructure/services/logger"
import SidebarTabButton from "./button/SidebarTabButton"
import { COLORS } from "../../presentation/constants/colors"

const Sidebar: React.FC<{
  isSidebarOpen: boolean
  toggleSidebar: () => void
}> = ({ isSidebarOpen, toggleSidebar }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const apiService = container.resolve<ApiService>("apiService")
  const logger = container.resolve<Logger>("logger")

  const handleProductManagementClick = async () => {
    try {
      const metadata = await apiService.getMetadata()
      sessionStorage.setItem("metadata", JSON.stringify(metadata))
      navigate("/products")
    } catch (error) {
      logger.error("Error fetching metadata:", error)
    }
  }

  // Defined the sidebar items
  const sidebarItems = [
    {
      path: "/dashboard",
      label: "Terminal Management",
      icon: <Building size={20} />,
      matchPaths: ["/dashboard", "/terminal/:terminalId"],
    },
    {
      path: "/orders",
      label: "Order Management",
      icon: <ShoppingCart size={20} />,
    },
    { path: "/members", label: "Member Management", icon: <Users size={20} /> },
    {
      path: "/products",
      label: "Product Management",
      icon: <Package size={20} />,
      onClick: handleProductManagementClick,
    },
  ]

  // Function to check if the current path matches any of the allowed paths for a sidebar item
  const isActiveTab = (item: {
    path: string
    matchPaths?: string[]
  }): boolean => {
    const pathsToMatch = item.matchPaths || [item.path]
    return pathsToMatch.some((path) => {
      if (path.includes(":terminalId")) {
        return location.pathname.startsWith(path.split(":")[0])
      }
      return location.pathname === path
    })
  }

  return (
    <div
      className={`fixed inset-y-0 left-0 w-56 sm:w-64 shadow-lg transform transition-transform duration-300 z-50 
        ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"} 
        md:translate-x-0 md:relative flex flex-col`}
      style={{ backgroundColor: COLORS.SIDEBAR_BG }}
    >
      {/* Logo Section */}
      <div className="p-4 sm:p-6 flex items-center justify-center">
        <div className="bg-white p-1 sm:p-2 rounded-lg">
          <img
            data-automation-id="sidebar-company-logo-img"
            id="sidebar-company-logo-img"
            src={companylogo}
            alt="Admin Logo"
            className="w-24 sm:w-32"
          />
        </div>
      </div>

      {/* Sidebar Items */}
      <div className="flex-grow overflow-y-auto">
        <ul className="mt-2 sm:mt-4">
          {sidebarItems.map((item) => (
            <li key={item.path} className="my-1 sm:my-2">
              <SidebarTabButton
                data-automation-id="sidebar-navigation-tab-button"
                id="sidebar-navigation-tab-button"
                path={item.path}
                label={item.label}
                icon={item.icon}
                onClick={item.onClick}
                matchPaths={item.matchPaths}
              />
            </li>
          ))}
        </ul>
      </div>

      {/* Logout Button (Fixed at Bottom) */}
      <div className="p-4 ">
        <LogoutButton
          data-automation-id="sidebar-logout-portal-button"
          id="sidebar-logout-portal-button"
          className="w-full bg-red-500 text-white px-3 py-1 sm:px-4 sm:py-2 rounded-lg hover:bg-red-600 transition-all duration-300 text-sm sm:text-base"
        />
      </div>
    </div>
  )
}

export default Sidebar